import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import ManageTeam from "./ManageTeam";
import { FaUsersCog, FaPlus, FaEdit, FaTrashAlt, FaSearch } from "react-icons/fa";

const passwordLetterMessage = "Password must contain at least 6 letters";
const countPasswordLetters = (value) => (String(value).match(/[A-Za-z]/g) || []).length;

function ManageStaff() {

    const [staff, setStaff] = useState([]);
    const [filteredStaff, setFilteredStaff] = useState([]);
    const [show, setShow] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [editingStaff, setEditingStaff] = useState(null);
    const [showEdit, setShowEdit] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchStaff();
    }, []);

    const fetchStaff = async () => {
        try {
            setLoading(true);
            console.log(`Fetching staff from: ${API_BASE_URL}/user/team-list`);
            const response = await axios.get(`${API_BASE_URL}/user/team-list`);
            console.log("API Response:", response.data);
            console.log("Response length:", response.data?.length);
            
            if (response.data && Array.isArray(response.data)) {
                setStaff(response.data);
                setFilteredStaff(response.data);
                console.log("Staff set successfully:", response.data.length, "members");
            } else {
                console.log("Invalid response format:", response.data);
                setStaff([]);
                setFilteredStaff([]);
            }
        } catch (error) {
            console.error("Error fetching staff:", error);
            console.error("Error status:", error.response?.status);
            console.error("Error details:", error.response?.data);
            setStaff([]);
            setFilteredStaff([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const filtered = staff.filter(member => 
            member.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.username?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredStaff(filtered);
    }, [searchTerm, staff]);

    const handleEdit = (staffMember) => {
        setEditingStaff(staffMember);
        setShowEdit(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this staff member?")) {
            try {
                await axios.delete(`${API_BASE_URL}/user/staff/${id}`);
                fetchStaff(); 
            } catch (error) {
                console.error("Error deleting staff:", error);
            }
        }
    };

    return (
        <div className="container-fluid">

            <div className="d-flex justify-content-between mb-4">

                <h4>
                    <FaUsersCog className="me-2 text-success" />
                    Manage Staff
                </h4>

                <div className="d-flex gap-2">
                    <button
                        className="btn btn-outline-primary"
                        onClick={fetchStaff}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                    <button
                        className="btn btn-success"
                        onClick={() => setShow(true)}
                    >
                        <FaPlus className="me-2" />
                        Add Staff
                    </button>
                </div>

            </div>

            {/* Search Bar */}
            <div className="mb-3">
                <div className="input-group">
                    <span className="input-group-text">
                        <FaSearch />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, email, or username..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <table className="table table-bordered">

                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Username</th>
                        <th>Permissions</th>
                        <th>Actions</th>
                    </tr>
                </thead>

                <tbody>

                    {filteredStaff?.length > 0 ? (

                        filteredStaff.map((s) => (
                            <tr key={s._id}>
                                <td>{s.fullName}</td>
                                <td>{s.email}</td>
                                <td>{s.username}</td>
                                <td>{s.permissions?.join(", ")}</td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-warning me-2"
                                        onClick={() => handleEdit(s)}
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDelete(s._id)}
                                    >
                                        <FaTrashAlt />
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="5" className="text-center">
                                {searchTerm ? "No matching staff found" : "No staff found"}
                            </td>
                        </tr>
                    )}

                </tbody>

            </table>

            <ManageTeam
                show={show}
                onClose={() => setShow(false)}
                onSuccess={fetchStaff}
            />

            {/* Edit Staff Modal */}
            {showEdit && editingStaff && (
                <EditStaffModal
                    staff={editingStaff}
                    onClose={() => {
                        setShowEdit(false);
                        setEditingStaff(null);
                    }}
                    onUpdate={fetchStaff}
                />
            )}

        </div>
    )
}

// Edit Staff Modal Component
function EditStaffModal({ staff, onClose, onUpdate }) {
    const [formData, setFormData] = useState({
        fullName: staff?.fullName || "",
        email: staff?.email || "",
        username: staff?.username || "",
        password: ""
    });
    const [permissions, setPermissions] = useState(staff?.permissions || []);

    const permissionList = [
        "Add / Edit Schools",
        "Add / Edit Uniform Products", 
        "View Stock Inventory",
        "View Total Revenue",
        "View All Orders"
    ];

    const togglePermission = (perm) => {
        if (permissions.includes(perm)) {
            setPermissions(permissions.filter(p => p !== perm));
        } else {
            setPermissions([...permissions, perm]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleUpdate = async () => {
        if (!formData.fullName || !formData.username) {
            return alert("Required fields missing");
        }
        if (formData.password && countPasswordLetters(formData.password) < 6) {
            return alert(passwordLetterMessage);
        }

        try {
            await axios.put(`${API_BASE_URL}/user/staff/${staff._id}`, {
                ...formData,
                permissions
            });
            
            alert("Staff updated successfully");
            onUpdate();
            onClose();
        } catch (error) {
            console.error("Error updating staff:", error);
            alert(error.response?.data?.message || "Error updating staff");
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Staff</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <div className="mb-3">
                            <label className="form-label">Full Name</label>
                            <input
                                type="text"
                                className="form-control"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input
                                type="email"
                                className="form-control"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Username</label>
                            <input
                                type="text"
                                className="form-control"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">New Password (leave empty to keep current)</label>
                            <input
                                type="password"
                                className="form-control"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Leave empty to keep current password"
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Permissions</label>
                            {permissionList.map((perm) => (
                                <div key={perm} className="form-check mb-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={permissions.includes(perm)}
                                        onChange={() => togglePermission(perm)}
                                    />
                                    <label className="form-check-label ms-2">{perm}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-success" onClick={handleUpdate}>
                            Update Staff
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ManageStaff;
